import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { API, fmtMoney } from "@/lib/data";

const STATUSES = ["awaiting_deposit", "deposit_paid", "scheduled", "installed", "completed", "cancelled"];

const statusCls = {
  awaiting_deposit: "border-rose-500/40 bg-rose-950/50 text-rose-300",
  deposit_paid: "border-amber-500/40 bg-amber-950/50 text-amber-300",
  scheduled: "border-sky-500/40 bg-sky-950/50 text-sky-300",
  installed: "border-emerald-500/40 bg-emerald-950/50 text-emerald-300",
  completed: "border-emerald-500/40 bg-emerald-900/60 text-emerald-200",
  cancelled: "border-stone-500/40 bg-stone-900/60 text-stone-400",
};

export const AdminBookingsTab = ({ token }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(data.bookings);
    } catch {
      toast.error("Could not load bookings");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const schedule = Object.entries(
    bookings
      .filter((b) => b.install_date && b.status !== "cancelled")
      .reduce((acc, b) => {
        acc[b.install_date] = (acc[b.install_date] || 0) + 1;
        return acc;
      }, {})
  ).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-amber-50">Bookings</h2>
        <button onClick={load} data-testid="admin-refresh-button" className="flex items-center gap-2 rounded-full border border-emerald-500/30 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-emerald-100 transition-all hover:border-amber-400/50">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="admin-schedule-chips">
        <span className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-emerald-100/40">Booked dates:</span>
        {schedule.length === 0 && <span className="text-xs text-emerald-100/40">Nothing scheduled yet</span>}
        {schedule.map(([d, c]) => (
          <span key={d} className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 font-mono-x text-[11px] tracking-wider text-amber-300">
            {new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {c} job{c > 1 ? "s" : ""}
          </span>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-amber-500/20" data-testid="admin-bookings-table">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-[#0B1D14] font-mono-x text-[10px] uppercase tracking-[0.18em] text-amber-300">
            <tr>
              {["Created", "Customer", "Package", "Install Date", "Deposit", "Add-on", "Status", "Update"].map((h) => (
                <th key={h} className="px-4 py-3.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-amber-500/10 bg-[#08170F]">
            {bookings.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-emerald-100/40">No bookings yet. They'll appear here as soon as a customer pays a deposit.</td></tr>
            )}
            {bookings.map((b) => (
              <tr key={b.id} className="align-top" data-testid="admin-booking-row">
                <td className="px-4 py-4 font-mono-x text-xs text-emerald-100/60">
                  {new Date(b.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </td>
                <td className="px-4 py-4">
                  <p className="font-semibold text-amber-50">{b.name}</p>
                  <p className="text-xs text-emerald-100/60">{b.phone}</p>
                  <p className="text-xs text-emerald-100/60">{b.email}</p>
                  <p className="mt-1 max-w-[200px] text-xs italic text-emerald-100/40">{b.address}</p>
                  {b.notes && <p className="mt-1 max-w-[200px] text-xs text-amber-200/50">"{b.notes}"</p>}
                </td>
                <td className="px-4 py-4">
                  <p className="text-amber-50">{b.package_name}</p>
                  <p className="text-xs text-emerald-100/50">{fmtMoney(b.total)} total</p>
                </td>
                <td className="px-4 py-4 text-xs text-emerald-100/70">
                  {b.install_date
                    ? new Date(b.install_date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
                    : "—"}
                </td>
                <td className="px-4 py-4">
                  <p className="font-bold text-amber-400">{fmtMoney(b.deposit)}</p>
                  <p className={`text-[10px] font-mono-x uppercase tracking-widest ${b.deposit_paid ? "text-emerald-400" : "text-rose-400"}`}>
                    {b.deposit_paid ? "Paid" : "Unpaid"}
                  </p>
                </td>
                <td className="px-4 py-4 text-xs text-emerald-100/70">{b.addon ? "Storage add-on" : "—"}</td>
                <td className="px-4 py-4">
                  <span className={`rounded-full border px-3 py-1 font-mono-x text-[10px] uppercase tracking-widest ${statusCls[b.status] || statusCls.awaiting_deposit}`}>
                    {b.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <select
                    value={b.status}
                    onChange={(e) => setStatus(b.id, e.target.value)}
                    data-testid={`admin-status-select-${b.id.slice(0, 8)}`}
                    className="rounded-lg border border-emerald-500/25 bg-[#06130D] px-2 py-1.5 text-xs text-amber-50 outline-none"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
